import {
  Check as CheckIcon,
  Close as CloseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputBase,
  MenuItem,
  Select,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import type { AccessLevel, CatalogSelectOption, TranslateFn } from "../types";
import { toTestId } from "../utils/catalog";

type ShareEmailInputProps = {
  t: TranslateFn;
  emails: string[];
  emailInput: string;
  accessLevel: AccessLevel;
  accessLevels: CatalogSelectOption<AccessLevel>[];
  blockedEmails: string[];
  submitting: boolean;
  canShare: boolean;
  canSubmitModel: boolean;
  onEmailInputChange: (value: string) => void;
  onInputBlur: () => void;
  onInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onRemoveEmail: (email: string) => void;
  onAccessLevelChange: (value: AccessLevel) => void;
  onShare: () => void;
  resolvedProjectEmails: Record<string, boolean>;
};

const ShareEmailInput = ({
  t,
  emails,
  emailInput,
  accessLevel,
  accessLevels,
  blockedEmails,
  submitting,
  canShare,
  canSubmitModel,
  onEmailInputChange,
  onInputBlur,
  onInputKeyDown,
  onRemoveEmail,
  onAccessLevelChange,
  onShare,
  resolvedProjectEmails,
}: ShareEmailInputProps) => (
  <Box className="flex items-center gap-2">
    <Box className="flex-1 min-h-[60px] flex flex-wrap items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--background)] px-2 py-2">
      {emails.map((email) => {
        const hasError =
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ||
          blockedEmails.includes(email);
        const isAdded = resolvedProjectEmails[email];

        return (
          <Chip
            data-testid={`ai-catalog-share-email-chip-${toTestId(email)}`}
            key={email}
            size="small"
            label={email}
            icon={
              hasError ? (
                <WarningIcon className="!text-white" />
              ) : isAdded ? (
                <CheckIcon className="text-current" />
              ) : undefined
            }
            onDelete={() => onRemoveEmail(email)}
            deleteIcon={
              <CloseIcon
                data-testid={`ai-catalog-share-remove-email-${toTestId(email)}`}
              />
            }
            className={`h-6 rounded-[8px] ${
              isAdded
                ? "bg-[var(--secondary)] text-[var(--secondary-foreground)] [&_.MuiChip-deleteIcon]:text-[var(--secondary-foreground)]"
                : hasError
                  ? "bg-[var(--destructive)] text-[var(--destructive-foreground)] [&_.MuiChip-deleteIcon]:text-[var(--destructive-foreground)]"
                  : "bg-[var(--muted)] text-[var(--secondary-foreground)] [&_.MuiChip-deleteIcon]:text-[var(--secondary-foreground)]"
            } [&_.MuiChip-label]:px-1.5 [&_.MuiChip-label]:text-[13px]`}
          />
        );
      })}
      <InputBase
        data-testid="ai-catalog-share-email-input"
        value={emailInput}
        onChange={(event) => onEmailInputChange(event.target.value)}
        onBlur={onInputBlur}
        onKeyDown={onInputKeyDown}
        placeholder={emails.length ? "" : t("aiCatalog.shareDialog.addEmails")}
        disabled={submitting}
        inputProps={{
          "data-testid": "ai-catalog-share-email-input-field",
        }}
        className="min-w-[120px] flex-1 text-[var(--foreground)] [&_input]:p-0 [&_input]:text-sm"
      />

      <Select
        data-testid="ai-catalog-share-access-select"
        value={accessLevel}
        onChange={(event: SelectChangeEvent<AccessLevel>) =>
          onAccessLevelChange(event.target.value as AccessLevel)
        }
        IconComponent={KeyboardArrowDownIcon}
        disabled={submitting}
        className="h-10 min-w-[120px] rounded-[8px] bg-[var(--muted)] text-[var(--foreground)] [&_fieldset]:border-0 [&_.MuiSelect-select]:px-[10px] [&_.MuiSelect-select]:py-2 [&_.MuiSelect-select]:text-sm [&_.MuiSvgIcon-root]:text-[var(--foreground)]"
      >
        {accessLevels.map((option) => (
          <MenuItem
            data-testid={`ai-catalog-share-access-option-${option.value}`}
            key={option.value}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
    <Button
      data-testid="ai-catalog-share-submit-button"
      variant="contained"
      onClick={onShare}
      disabled={!canShare || !canSubmitModel}
      className="h-10 min-w-[88px] rounded-[8px] bg-[var(--primary)] text-[var(--primary-foreground)] normal-case disabled:bg-[var(--primary)] disabled:text-[var(--primary-foreground)] disabled:opacity-[0.45]"
    >
      {submitting ? (
        <CircularProgress size={18} />
      ) : (
        t("aiCatalog.shareDialog.share")
      )}
    </Button>
  </Box>
);

export default ShareEmailInput;
